import React from 'react';
import config from '../config';
import { useAuth } from '../auth/AuthContext';

const defaultFlags = Object.freeze({
  unified_reads: false,
  unified_writes: false,
  publication_workflows: false,
  legacy_navigation: true,
});

const FeatureFlagsContext = React.createContext({
  flags: defaultFlags,
  loading: false,
  error: null,
  reload: () => Promise.resolve(),
});

export const FeatureFlagsProvider = ({ children }) => {
  const { fetchWithAuth } = useAuth();
  const [flags, setFlags] = React.useState(defaultFlags);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadFlags = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/features`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load application features');
      }
      setFlags({
        unified_reads: payload.unified_reads === true,
        unified_writes: payload.unified_writes === true,
        publication_workflows: payload.publication_workflows === true,
        legacy_navigation: payload.legacy_navigation !== false,
      });
    } catch (loadError) {
      // The compatibility UI remains available when feature discovery fails.
      setFlags(defaultFlags);
      setError(loadError.message || 'Failed to load application features');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const value = React.useMemo(() => ({
    flags,
    loading,
    error,
    reload: loadFlags,
  }), [error, flags, loadFlags, loading]);

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = () => React.useContext(FeatureFlagsContext);

export default FeatureFlagsContext;
